/**
 * /Recon — application surface for SignumCyber engagements.
 *
 * Bucket SC Phase SC5 (this commit) stripped the standalone marketing
 * pages from this codebase. Marketing now lives at signumcyber.com;
 * /Recon is the app surface only — commission, payment, verify,
 * validate, and the authenticated portal.
 *
 * The legacy /authorize/:token engagement-letter flow was removed in
 * Bucket QL — the click-to-accept Authorization to Assess at intake
 * (commission-form's attestation step) is the legal anchor now, captured
 * in the engagement_attestation ledger before Stripe checkout fires.
 *
 * Routes:
 *   PUBLIC (no shell, no portal auth)
 *   /payment/success | /cancelled    Stripe return URLs
 *   /validate                        Recipient token-validation entry
 *   /validate/success                Recipient viewer (cookie-authed)
 *
 *   PORTAL AUTH (no shell — pages render their own AuthCard)
 *   /portal/login                    Sign in
 *   /portal/signup                   Self-serve signup
 *   /portal/verify-email/:token      Email verification confirmation
 *   /portal/setup/:setupToken        First-time password setup
 *   /portal/forgot                   Request password reset
 *   /portal/reset/:resetToken        Set new password from reset link
 *
 *   AUTHENTICATED PORTAL (PortalShell)
 *   /portal                          Dashboard
 *   /portal/commission               Commission a Quick Check (configurator)
 *   /portal/commission/success       Post-submit confirmation
 *   /portal/engagements/:caseId      Engagement detail
 *   /portal/engagements/:caseId/reports/:reportId/html
 *                                    Interactive report viewer
 *   /portal/documents                Cross-engagement deliverables
 *   /portal/account                  Profile + sign out
 *   /portal/trusted-recipients       Trust bank for share recipients
 *
 *   REDIRECTS (legacy public marketing URLs)
 *   /                                → /portal/login (in dev)
 *   /pricing, /attack-surface, etc.  → /portal/login
 *   In production, Caddy will 308 these to signumcyber.com/recon/* .
 *   Until then, the React-side redirect lands users on the login page
 *   so bookmarks of old URLs don't 404.
 */
import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ClientAuthProvider } from "@/auth/client-auth-context";
import { PaymentSuccessPage } from "@/pages/payment/payment-success";
import { PaymentCancelledPage } from "@/pages/payment/payment-cancelled";
import { PortalShell } from "@/pages/portal/portal-shell";
import { PortalDashboard } from "@/pages/portal/dashboard";
import { EngagementDetail } from "@/pages/portal/engagement-detail";
import { CommissionPage } from "@/pages/commission/CommissionPage";
import { PortalCommissionSuccess } from "@/pages/portal/commission-success";
import { ActionTrackerPage } from "@/pages/portal/action-tracker";
import { PortalDocuments } from "@/pages/portal/documents";
import { PortalAccount } from "@/pages/portal/account";
import { TrustedRecipients } from "@/pages/portal/trusted-recipients";
import { ValidatePage } from "@/pages/validate/validate-page";
import { ValidateSuccessPage } from "@/pages/validate/validate-success-page";
import { StatusPage } from "@/pages/status/status-page";
import {
  LoginPage,
  SignupPage,
  VerifyEmailPage,
  SetupPage,
  ForgotPage,
  ResetPage,
} from "@/pages/portal/auth-pages";

// Report viewer is route-level code-split: it pulls in chart libraries
// (Recharts in QH3) and finding-renderer components that the rest of the
// portal doesn't need. Lazy import means the ~90KB chart bundle only loads
// when a client opens an HTML report, not on every portal page.
const ReportViewer = lazy(() =>
  import("@/pages/portal/report-viewer").then((m) => ({ default: m.ReportViewer })),
);

function ReportViewerLoading() {
  return (
    <div style={{ padding: "var(--space-12) var(--space-6)", textAlign: "center", color: "var(--fg3)" }}>
      Loading report viewer…
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ClientAuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Legacy marketing URLs — redirected to the portal login.
                In production, Caddy 308s these to signumcyber.com/recon/*
                before the React app sees them; this fallback covers dev
                + any direct hits that bypass Caddy. */}
            <Route path="/" element={<Navigate to="/portal/login" replace />} />
            <Route path="/pricing" element={<Navigate to="/portal/login" replace />} />
            <Route path="/attack-surface" element={<Navigate to="/portal/login" replace />} />
            <Route path="/personal-exposure" element={<Navigate to="/portal/login" replace />} />
            <Route path="/commission" element={<Navigate to="/portal/login" replace />} />

            {/* Public app surface — no shell. Each page renders its own
                minimal brand chrome via the recon-hero / recon-section
                primitives. */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancelled" element={<PaymentCancelledPage />} />

            {/* Public status page (Bucket QS-1). No auth, no shell. */}
            <Route path="/status" element={<StatusPage />} />

            {/* Public recipient-validation surface (Bucket Q2.5).
                Recipients aren't /Recon users; they have a token + their
                email. */}
            <Route path="/validate" element={<ValidatePage />} />
            <Route path="/validate/success" element={<ValidateSuccessPage />} />

            {/* Portal auth pages — no shell. The AuthCard in auth-pages.tsx
                provides its own minimal brand lockup. */}
            <Route path="/portal/login" element={<LoginPage />} />
            <Route path="/portal/signup" element={<SignupPage />} />
            <Route path="/portal/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/portal/setup/:setupToken" element={<SetupPage />} />
            <Route path="/portal/forgot" element={<ForgotPage />} />
            <Route path="/portal/reset/:resetToken" element={<ResetPage />} />

            {/* Authenticated portal — PortalShell redirects to login if no token */}
            <Route element={<PortalShell />}>
              <Route path="/portal" element={<PortalDashboard />} />
              <Route path="/portal/commission" element={<CommissionPage />} />
              <Route path="/portal/commission/success" element={<PortalCommissionSuccess />} />
              <Route path="/portal/engagements/:caseId" element={<EngagementDetail />} />
              <Route
                path="/portal/engagements/:caseId/reports/:reportId/html"
                element={
                  <Suspense fallback={<ReportViewerLoading />}>
                    <ReportViewer />
                  </Suspense>
                }
              />
              <Route
                path="/portal/engagements/:caseId/reports/:reportId/actions"
                element={<ActionTrackerPage />}
              />
              <Route path="/portal/documents" element={<PortalDocuments />} />
              <Route path="/portal/account" element={<PortalAccount />} />
              <Route path="/portal/trusted-recipients" element={<TrustedRecipients />} />
            </Route>

            {/* Catch-all for anything else — bounce to login. */}
            <Route path="*" element={<Navigate to="/portal/login" replace />} />
          </Routes>
        </BrowserRouter>
      </ClientAuthProvider>
    </QueryClientProvider>
  );
}
